import { Mail } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import BackButton from "../components/BackButton";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import EventService from "../services/APIs/EventService";

interface VolunteerSignup {
  id: string;
  volunteer_activity_id: string;
  volunteer_user_id: string;
  event_name?: string | null;
  first_name: string;
  last_name: string;
  email: string;
}

const Volunteering = () => {
  const [signups, setSignups] = useState<VolunteerSignup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVolunteerSignups = async () => {
      try {
        const response = await EventService.getVolunteerSignupList(false);
        if (response?.success && Array.isArray(response.data)) {
          setSignups(response.data);
        } else {
          setSignups([]);
        }
      } catch (error) {
        console.error("Failed to fetch volunteer signup list:", error);
        setSignups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteerSignups();
  }, []);

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div>
              <h1 className="text-lg font-bold text-primary md:text-xl">
                Volunteering
              </h1>
              <p className="text-xs text-muted-foreground">
                Find opportunities to help
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        {loading && (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Loading volunteer signups...
          </div>
        )}

        {!loading && signups.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No volunteer signups found for your account.
          </div>
        )}

        {!loading && signups.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {signups.map(signup => (
              <Card
                key={signup.id}
                className="transition-all"
              >
                <CardHeader>
                  <div className="mb-2">
                    <Badge variant="secondary">Volunteer Signup</Badge>
                  </div>
                  <CardTitle className="text-lg">
                    {signup.event_name?.trim() ? signup.event_name : "#######"}
                  </CardTitle>
                  <CardDescription>
                    {signup.first_name} {signup.last_name}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>{signup.email}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Volunteering;
